import { IStringLocalizer } from "../../../utilities/IStringLocalizer";
import { ConcernElement } from "../../../store/Models/Surveys/SurveyDataModel";
import { useState, useEffect } from "react";
import { ToxnotServiceClient } from "../../../store/ToxnotServiceClient";
import { CategoriesProvider } from "../../../store/Managers/CategoriesProvider/CategoriesProvider";
import { PercentRange } from "../../../utilities/PercentRange";
import { LoadingSpinner, LoadingSpinnerSize } from "../LoadingSpinner";

import "./styles.css";

interface ConcernElementsProps {
    localizer: IStringLocalizer;
    existingConcernElements: ConcernElement[];
    cultureName: string; //used to display the concern element concentration
    registerPopoversCb: (popoverIds: string[]) => void;
}

/// renders a readonly table of concern elements based on the existingConcernElements prop

const ConcernElements: React.FC<ConcernElementsProps> = ({ localizer, existingConcernElements, cultureName, registerPopoversCb }) => {
    const [currentConcernElements, setCurrentConcernElements] = useState(existingConcernElements);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [materialCategoriesDescriptionsList, setMaterialCategoriesDescriptionsList] = useState([] as { [codeId: number]: string });
    const [mixtureCategoriesDescriptionsList, setMixtureCategoriesDescriptionsList] = useState([] as { [codeId: number]: string });

    useEffect(() => {
        setCurrentConcernElements(existingConcernElements);
    }, [existingConcernElements]);

    useEffect(() => {
        (async () => {
            const serviceClient = new ToxnotServiceClient();
            const result = await serviceClient.scipIuclidStaticDataGet();
            const categoryProvider = new CategoriesProvider();

            if (!result.success || result.data === null) {
                throw new Error("Scip data was not valid");
            }

            setMaterialCategoriesDescriptionsList(categoryProvider.getCategoryTree(result.data.materialCategoriesTreeRoot));
            setMixtureCategoriesDescriptionsList(categoryProvider.getCategoryTree(result.data.mixtureCategoriesTreeRoot));
            setDataLoaded(true);

            registerPopoversCb(['mixture-category-popover', 'material-category-popover']);
        })();
    }, []);

    const getConcentration = (concernElement: ConcernElement) => {
        return new PercentRange(concernElement.concentrationRange.lowerBound, concernElement.concentrationRange.upperBound).toString(cultureName, 2);
    }

    return (
        <>
            {!dataLoaded &&
                <LoadingSpinner size={LoadingSpinnerSize.Small} />
            }
            {dataLoaded &&
                <>
                    <h4> {localizer.localizeString("Concern Elements")}</h4>
                    <div className="table-container">
                        <table className="table toxnot-table-v2">
                            <thead>
                                <tr key={"concern-element"}>
                                    <th key={"substance-name"}>{localizer.localizeString("Name")}</th>
                                    <th key={"cas"}>{localizer.localizeString("CASRN")}</th>
                                    <th key={"concentration"}>{localizer.localizeString("Concentration Range")}</th>
                                    <th key={"mixture"}>
                                        {localizer.localizeString("Mixture Category")}
                                        <a id="mixture-category-popover" data-container="body" role="button" className="m-l-5" data-toggle="popover" data-html="true" data-placement="bottom" data-content={localizer.localizeString("Edit this through the SCIP Concern Element Mixture Category column in the Ingredient Disclosure Data editor")}>
                                            <img height="14" src="/images/help-icon-dark-gray.png" />
                                        </a>
                                    </th>
                                    <th key={"material"}>
                                        {localizer.localizeString("Material Category")}
                                        <a id="material-category-popover" data-container="body" role="button" className="m-l-5" data-toggle="popover" data-html="true" data-placement="bottom" data-content={localizer.localizeString("Edit this through the SCIP Concern Element Material Category column in the Ingredient Disclosure Data editor")}>
                                            <img height="14" src="/images/help-icon-dark-gray.png" />
                                        </a>
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="data-table">
                                {currentConcernElements.map((c, index) =>
                                (
                                    <tr key={index}>
                                        <td key={"substance-name-" + index}>{c.referenceSubstanceName}</td>
                                        <td key={"cas-" + index}>{c.casNumber}</td>
                                        <td key={"concentration-" + index}>{getConcentration(c)}</td>
                                        <td key={"mixture-" + index}>
                                            {c.mixtureCategoryId !== null && c.mixtureCategoryId !== undefined ? mixtureCategoriesDescriptionsList[parseInt(c.mixtureCategoryId)] : ''}
                                        </td>
                                        <td key={"mateial-" + index}>
                                            {c.materialCategory?.categoryId !== null && c.materialCategory?.categoryId !== undefined ? materialCategoriesDescriptionsList[parseInt(c.materialCategory.categoryId)] : ''}
                                        </td>
                                    </tr>
                                )
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            }
        </>
    );
};

export default ConcernElements;